import React, { useMemo } from "react";
import { SharedValue, useDerivedValue } from "react-native-reanimated";
import { WidgetPos } from "../../Storecast/Storecast/types";
import { EmptyPosition } from "../../Storecast/Storecast/EmptyPositions/EmptyPosition";
import { EmptyPositionT } from "../../Storecast/Storecast/EmptyPositions/EmptyPosType";
import { AHText, useLayoutContext, useTheme } from "arkh";
import { FormattedMessage } from "react-intl";

export const StorecastSpaceEmptyPositions = React.memo(
  StorecastSpaceEmptyPositionsC
);

function StorecastSpaceEmptyPositionsC(props: {
  positions: SharedValue<WidgetPos[]>;
  unitSize: number;
  isEditing: boolean;
  numberOfRows: number;
  onAddWidget: (_: { y: number; h: number }) => void;
}) {
  const tiles = useMemo(
    () =>
      new Array(props.numberOfRows)
        .fill(0)
        .map(() => ({ active: false, x: 0, y: 0, c: 0, w: 0, h: 0 })),
    [props.numberOfRows]
  );
  const layout = useLayoutContext();
  const emptyPositions = useDerivedValue<EmptyPositionT[]>(() => {
    const emptyPos = props.positions.value
      .reduce(
        (empty, position) => {
          return empty.filter(
            (e) => e < position.y || e >= position.y + position.h
          );
        },
        new Array(props.numberOfRows).fill(0).map((_, i) => i)
      )
      .reduce(
        (emptyRich, emptyTile) => {
          if (emptyRich.length === 0) {
            return [{ start: emptyTile, end: emptyTile + 1 }];
          }
          const lastEmpty = emptyRich[emptyRich.length - 1];
          if (lastEmpty.end === emptyTile) {
            return [
              ...emptyRich.slice(0, emptyRich.length - 1),
              { start: lastEmpty.start, end: lastEmpty.end + 1 },
            ];
          } else {
            return [...emptyRich, { start: emptyTile, end: emptyTile + 1 }];
          }
        },
        [] as { start: number; end: number }[]
      )
      .map(({ start, end }) => ({
        active: true,
        y: start,
        h: end - start,
        x: 0,
        w: 2,
        c: 0,
      }));
    return tiles.map((tile, i) =>
      emptyPos[i] ? { ...emptyPos[i], active: true } : tile
    );
  });
  const theme = useTheme();

  return (
    <>
      {tiles.map((_, index) => (
        <EmptyPosition
          disabled={!props.isEditing}
          key={index}
          positions={emptyPositions}
          positionIndex={index}
          unitSize={props.unitSize}
          spaceBetweenColumns={layout.space.medium * 2}
          onPress={() =>
            props.onAddWidget({
              y: emptyPositions.value[index].y,
              h: emptyPositions.value[index].h,
            })
          }
          contentStyle={
            props.isEditing
              ? undefined
              : {
                  backgroundColor: theme["surface-backgroundControl"],
                  borderColor: theme["surface-backgroundControl"],
                }
          }
        >
          {props.isEditing ? null : (
            <AHText type="title-small" color="text-secondary">
              <FormattedMessage
                defaultMessage="Press edit to add widget"
                description="StorecastSpaceEmptyPosition edit cta text"
              />
            </AHText>
          )}
        </EmptyPosition>
      ))}
    </>
  );
}
